// People, profiles, follows, blocks, invitations and activity: the social
// lane's calls (SIGNED_IN_PLAN section 5, W4) on top of the one client.
// Follow is one way (d3): POST /add_friend or /remove_friend; a 406 from
// /add_friend means already following.
import { apiDelete, apiGet, apiPost } from "./client.js";
import { apiHeaders } from "../authHeaders.js";
import { safetyRequestFor, blockedUsernames, buildReportPayload } from "../safety.js";

const base = process.env.VUE_APP_API_URL || "";
const enc = encodeURIComponent;
const list = (v) => (Array.isArray(v) ? v : []);

// ---- people

/** Who you follow, as detail rows when the server has them, else names. */
export async function getFollowing() {
  try {
    const rows = await apiGet("/friends", { detail: 1 });
    if (Array.isArray(rows)) return rows;
  } catch (e) {
    if (e && e.status === 401) throw e;
  }
  return list(await apiGet("/friends"));
}

/** Everyone a name matches, as detail rows when the server has them. */
export async function searchUsers(query) {
  try {
    const rows = await apiGet("/search_users", { search_query: query, detail: 1 });
    if (Array.isArray(rows)) return rows;
  } catch (e) {
    if (e && e.status === 401) throw e;
  }
  return list(await apiGet("/search_users", { search_query: query }));
}

/** People you played with, newest first (optional on older servers). */
export async function getRecentPeople(limit = 50) {
  const rows = await apiGet("/people/recent", { limit }, { optional: true });
  return Array.isArray(rows) ? rows : [];
}

export function follow(username) {
  return apiPost("/add_friend", { friend_username: username }).catch((e) => {
    if (e && e.status === 406) return null;
    throw e;
  });
}

// 406 is "not following": the end state you asked for, as follow's 406
// (L6).
export function unfollow(username) {
  return apiPost("/remove_friend", { friend_username: username }).catch((e) => {
    if (e && e.status === 406) return null;
    throw e;
  });
}

// ---- profiles

/** GET /user (B4 stats behind ?stats=1). A 404 throws: no one by that name. */
export function getUser(username, { stats = false } = {}) {
  return apiGet("/user", { username, stats: stats ? 1 : null });
}

/** Every session of theirs you can see, all pages (newest first). */
export async function getUserSessions(username, { pageLimit = 200, maxPages = 10 } = {}) {
  const out = [];
  let cursor = null;
  let total = null;
  for (let i = 0; i < maxPages; i++) {
    const res = await apiGet("/sessions", { username, view: "list", limit: pageLimit, cursor });
    if (Array.isArray(res)) return { sessions: res, total: res.length };
    const rows = list(res && res.sessions);
    out.push(...rows);
    if (total == null && res && Number.isFinite(res.total)) total = res.total;
    cursor = res && res.next_cursor;
    if (!cursor || !rows.length) break;
  }
  return { sessions: out, total: total ?? out.length };
}

/** Your own newest sessions (the Invite to… menu). */
export async function getMySessions(limit = 50) {
  const res = await apiGet("/sessions", { view: "list", limit });
  if (Array.isArray(res)) return res;
  return list(res && res.sessions);
}

export async function getUserLoops(username) {
  const res = await apiGet(`/user/${enc(username)}/loops`);
  if (Array.isArray(res)) return res;
  return list(res && res.loops);
}

export async function getCuratorPacks(username) {
  const res = await apiGet("/packs", { curator: username }, { optional: true });
  return Array.isArray(res) ? res : list(res && res.packs);
}

export async function getFavoriteSessions(username) {
  return list(await apiGet("/favorite_sessions", { username }));
}

export async function getFavoriteLoops(username) {
  return list(await apiGet("/favorite_loops", { username }));
}

// ---- you

export function setBio(text) {
  return apiPost("/bio", { bio_text: text });
}

export function setDisplayName(name) {
  return apiPost("/display_name", { display_name: name || null });
}

export function setProfilePicture(link) {
  return apiPost("/set_profile_picture", { picture_link: link });
}

/** POST /upload_image (multipart); resolves the picture's link. */
export async function uploadImage(file) {
  const form = new FormData();
  form.append("file", file);
  const headers = { ...apiHeaders(readToken()) };
  delete headers["Content-Type"];
  delete headers["content-type"];
  let res;
  try {
    res = await fetch(`${base}/api/upload_image`, { method: "POST", headers, body: form, credentials: "include" });
  } catch (e) {
    throw { status: 0, message: "Couldn't reach cloudloop.", body: null };
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw { status: res.status, message: (body && body.message) || "Upload failed", body };
  const link = body && body.data && body.data.results && body.data.results.picture_link;
  if (!link) throw { status: res.status, message: "The upload sent no picture link", body };
  return link;
}

function readToken() {
  try {
    return window.localStorage.getItem("JWT");
  } catch (e) {
    return null;
  }
}

// ---- blocks and reports

export async function getBlocked() {
  return blockedUsernames(await apiGet("/blocks"));
}

export function block(username) {
  const r = safetyRequestFor("block", username);
  return apiPost(r.path, {});
}

export function unblock(username) {
  const r = safetyRequestFor("unblock", username);
  return apiDelete(r.path);
}

export function report({ targetType, targetId, reason, details }) {
  return apiPost("/reports", buildReportPayload({ targetType, targetId, reason, details }));
}

// ---- invitations and activity

/** Pending, accepted and declined invitations (rows at data.results.results). */
export async function getInbox() {
  const res = await apiGet("/invitations/inbox", { status: "pending,accepted,declined" });
  if (Array.isArray(res)) return res;
  return list(res && res.results);
}

/**
 * What happened while you played. Null when the server has no feed (404,
 * 405 or 503): the Inbox then shows no activity sections, not an error.
 */
export async function getActivity({ since = null, limit = 50 } = {}) {
  try {
    const res = await apiGet("/activity", { since, limit }, { optional: true });
    if (res && res.comingSoon) return null;
    return Array.isArray(res) ? res : list(res && res.activity);
  } catch (e) {
    if (e && e.status === 503) return null;
    throw e;
  }
}

export function acceptInvitation(id) {
  return apiPost(`/invitations/${enc(id)}/accept`);
}

export function declineInvitation(id) {
  return apiPost(`/invitations/${enc(id)}/decline`);
}

export function joinSession(username, sessionName) {
  return apiPost("/join", { username, session_name: sessionName });
}

/** POST /invitations; a username or an email, and an optional note. */
export function sendInvitation({ sessionName, username = null, email = null, message = null }) {
  const body = { session_name: sessionName };
  if (username) body.invitee_username = username;
  if (email) body.invitee_email = email;
  if (message) body.message = message;
  return apiPost("/invitations", body);
}

/** The host takes someone out of a session (POST /session/remove_member). */
export function removeMember(sessionName, username) {
  return apiPost("/session/remove_member", { session_name: sessionName, username });
}

export default {
  getFollowing,
  searchUsers,
  getRecentPeople,
  follow,
  unfollow,
  getUser,
  getUserSessions,
  getMySessions,
  getUserLoops,
  getCuratorPacks,
  getFavoriteSessions,
  getFavoriteLoops,
  setBio,
  setDisplayName,
  setProfilePicture,
  uploadImage,
  getBlocked,
  block,
  unblock,
  report,
  getInbox,
  getActivity,
  acceptInvitation,
  declineInvitation,
  joinSession,
  sendInvitation,
  removeMember,
};
